import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Avatar } from '@mui/material';
import {
  Assignment,
  CheckCircle,
  AttachMoney,
  TrendingUp,
  AccessTime,
  Star
} from '@mui/icons-material';
import { brand, text, border } from '../../theme/colors';

/**
 * Enhanced Verifier Stat Cards
 * Modern gradient stat cards matching Contractor Dashboard design
 */
const EnhancedVerifierStatCards = ({ stats }) => {
  const statCards = [
    {
      title: 'Active Assignments',
      value: stats?.currentAssignments || 0,
      subtitle: `${stats?.totalVerifications || 0} total verifications`,
      icon: Assignment,
      accentColor: brand.primary,
      iconBg: `${brand.primary}1a`,
      trend: stats?.assignmentTrend || 0
    },
    {
      title: 'Completed This Month',
      value: stats?.completedThisMonth || 0,
      subtitle: `${stats?.completionRate || 0}% completion rate`,
      icon: CheckCircle,
      accentColor: '#FAA61A',
      iconBg: 'rgba(250, 166, 26, 0.1)',
      trend: stats?.completionTrend || 0
    },
    {
      title: 'Earnings This Month',
      value: `$${(stats?.earningsThisMonth || 0).toLocaleString()}`,
      subtitle: `$${(stats?.totalEarnings || 0).toLocaleString()} total earned`,
      icon: AttachMoney,
      accentColor: '#43B581',
      iconBg: 'rgba(67, 181, 129, 0.1)',
      trend: stats?.earningsTrend || 0
    },
    {
      title: 'Average Rating',
      value: (stats?.averageRating || 0).toFixed(1),
      subtitle: `${stats?.totalReviews || 0} reviews`,
      icon: Star,
      accentColor: brand.secondary,
      iconBg: 'rgba(155, 89, 182, 0.1)',
      trend: stats?.ratingTrend || 0
    }
  ];

  return (
    <Grid container spacing={3}>
      {statCards.map((card, index) => {
        const IconComponent = card.icon;
        return (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                backgroundColor: 'white',
                border: `1px solid ${border.light}`,
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 16px rgba(67, 181, 129, 0.15)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', my: 1, color: card.accentColor }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9aa5b1' }}>
                      {card.subtitle}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: card.iconBg,
                      width: 56,
                      height: 56
                    }}
                  >
                    <IconComponent sx={{ fontSize: 32, color: card.accentColor }} />
                  </Avatar>
                </Box>

                {/* Trend Indicator */}
                {card.trend !== 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <TrendingUp
                      sx={{
                        fontSize: 16,
                        transform: card.trend < 0 ? 'rotate(180deg)' : 'none',
                        color: card.trend < 0 ? '#e74c3c' : '#43B581'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 600 }}>
                      {Math.abs(card.trend)}% vs last month
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default EnhancedVerifierStatCards;
